import { useSyncExternalStore } from "react";
import { type BaseLocationHook } from "wouter";

export type StackEntry = {
  path: string;
  // Stable identity for the layer rendering this entry. Survives replacePath
  // so the screen component isn't remounted when only the path changes
  // (e.g. queue auto-advance swapping /video/a for /video/b).
  key: string;
};

const rootPath = "/";

let nextKey = 0;
let stack: StackEntry[] = [{ path: rootPath, key: "view-0" }];
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeNavigationStack(listener: () => void) {
  return subscribe(listener);
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function getStack() {
  return stack;
}

export function pushPath(path: string) {
  nextKey += 1;
  stack = [...stack, { path, key: `view-${nextKey}` }];
  emit();
}

// Swap the top entry's path in place (same layer, no push/pop animation).
export function replacePath(path: string) {
  const current = stack[stack.length - 1];

  if (!current || current.path === path) {
    return;
  }

  stack = [...stack.slice(0, -1), { ...current, path }];
  emit();
}

export function popPath() {
  if (stack.length <= 1) {
    return;
  }

  stack = stack.slice(0, -1);
  emit();
}

// Test helper: restore the pristine single-entry stack.
export function resetStack(path: string = rootPath) {
  nextKey = 0;
  stack = [{ path, key: "view-0" }];
  emit();
}

export function useNavigationStack() {
  return useSyncExternalStore(subscribe, getStack);
}

function navigate(to: string, options?: { replace?: boolean }) {
  if (options?.replace) {
    replacePath(to);
  } else {
    pushPath(to);
  }
}

// wouter custom location hook: the top of the stack is the current location.
// Mount with <Router hook={useStackLocation}> around the iPhone shell.
export const useStackLocation: BaseLocationHook = () => {
  const entries = useNavigationStack();
  const current = entries[entries.length - 1];

  return [current?.path ?? rootPath, navigate];
};
